import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  getPatientDocuments,
  uploadPatientDocument,
  deletePatientDocument,
  downloadPatientDocument,
  fileToBase64,
} from "../../../services/patientService";
import {
  CloudUploadIcon,
  DeleteIcon,
  ViewIcon,
  DocumentsIcon,
} from "../../../Icons";
import CollapsibleSection, {
  useCollapseOpenForCount,
} from "./CollapsibleSection";
import useConfirm from "../../../hooks/useConfirm";
import { displayDateUS } from "../../../utils/dateUtils";
import { PATIENT_SECTION_TONE, PATIENT_THEME } from "../patientTheme";
import {
  PATIENT_DOC_UPLOAD_ACCEPT,
  PATIENT_DOC_UPLOAD_TYPES_LABEL,
  validatePatientUploadFileClient,
} from "../patientUploadTypes";

const DOCUMENTS_TONE = PATIENT_SECTION_TONE;

const CATEGORIES = [
  { value: "GovernmentId", label: "Government ID" },
  { value: "InsuranceCard", label: "Insurance Card" },
  { value: "MedicalHistory", label: "Medical History" },
  { value: "Referral", label: "Referral" },
  { value: "Assessment", label: "Assessment" },
  { value: "PlanOfCare", label: "Plan of Care" },
  { value: "Consent", label: "Consent" },
  { value: "Prescription", label: "Prescription" },
  { value: "Contract", label: "Contract" },
  { value: "Others", label: "Others" },
];

const fieldClass =
  "w-full h-9 px-2.5 text-sm border border-slate-200 rounded-md bg-white outline-none transition-shadow focus:border-slate-400 focus:ring-2 focus:ring-slate-100";
const labelClass = "block text-[11px] font-semibold text-slate-600 mb-1";

/** White outlined action button — matches Demographics / Admission Docs add actions. */
const outlineActionBtn =
  "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md border bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const outlineBlueBtn = `${outlineActionBtn} text-indigo-700 border-indigo-300 hover:bg-indigo-50`;
const outlineNeutralBtn = `${outlineActionBtn} text-slate-700 border-slate-300 hover:bg-slate-50`;

const categoryLabel = (value) =>
  CATEGORIES.find((c) => c.value === value)?.label || value || "—";

const PatientDocumentsTab = ({
  patientId,
  chartId,
  canManage,
  onRefresh,
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [category, setCategory] = useState("Others");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const { confirm, confirmDialog } = useConfirm();

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPatientDocuments(patientId, {
        excludeCategory: "Authorization",
        ...(chartId ? { chartId } : {}),
      });
      setDocuments(res.result?.documents || res.documents || []);
    } catch (err) {
      toast.error(err.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [patientId, chartId]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const [sectionOpen, onSectionOpenChange] = useCollapseOpenForCount(
    documents.length,
    `${patientId}:${chartId || ""}`,
  );

  const resetForm = () => {
    setDocumentName("");
    setCategory("Others");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) {
      setFile(null);
      return;
    }
    const check = validatePatientUploadFileClient(picked);
    if (!check.ok) {
      toast.error(check.message);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(picked);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    const check = validatePatientUploadFileClient(file);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    setUploading(true);
    try {
      const fileBase64 = await fileToBase64(file);
      await uploadPatientDocument(patientId, {
        documentName: documentName.trim() || null,
        documentCategory: category,
        documentType: category,
        documentDate: null,
        fileBase64,
        fileName: file.name,
        ...(chartId ? { chartId: Number(chartId) } : {}),
      });
      toast.success("Document uploaded");
      resetForm();
      await loadDocs();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openFile = async (docId, intent) => {
    try {
      const res = await downloadPatientDocument(patientId, docId, intent);
      const url = res.result?.url || res.url;
      if (url) window.open(url, "_blank");
    } catch (err) {
      toast.error(err.message || "Unable to open file");
    }
  };

  const handleDelete = async (doc) => {
    const ok = await confirm({
      title: "Delete document?",
      message: `"${doc.documentName || doc.originalName || "This document"}" will be permanently removed. This cannot be undone.`,
    });
    if (!ok) return;
    try {
      await deletePatientDocument(patientId, doc.id);
      toast.success("Document deleted");
      await loadDocs();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const showActions = canManage;

  return (
    <div className="flex flex-col gap-3">
      {!canManage ? (
        <p className="px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg">
          Documents are read-only for your role. Contact Admin or OA to upload,
          view, or delete files.
        </p>
      ) : null}

      <CollapsibleSection
        title="Documents"
        icon={<DocumentsIcon size={14} />}
        tone={DOCUMENTS_TONE}
        open={sectionOpen}
        onOpenChange={onSectionOpenChange}
        badge={
          <span className="inline-flex items-center justify-center h-5 px-1.5 rounded text-[10px] font-semibold bg-slate-600 text-white tabular-nums">
            {documents.length} file{documents.length === 1 ? "" : "s"}
          </span>
        }
      >
        {canManage ? (
          <div className="px-3 py-3 border-b border-slate-100 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 items-end">
              <div className="lg:col-span-4">
                <label className={labelClass}>
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept={PATIENT_DOC_UPLOAD_ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className={`${outlineNeutralBtn} shrink-0`}
                  >
                    Select file
                  </button>
                  <span className="text-xs text-slate-500 truncate min-w-0">
                    {file ? file.name : "No file selected"}
                  </span>
                </div>
                <p className="mt-1 text-[10px] text-slate-500">
                  {PATIENT_DOC_UPLOAD_TYPES_LABEL} (incl. iPhone photos) · max 20
                  MB
                </p>
              </div>
              <div className="lg:col-span-3">
                <label className={labelClass}>
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  className={fieldClass}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lg:col-span-3">
                <label className={labelClass}>Document Name</label>
                <input
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder="Optional label"
                  className={fieldClass}
                />
              </div>
              <div className="lg:col-span-2 flex sm:justify-end">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading}
                  className={`${outlineBlueBtn} w-full lg:w-auto`}
                >
                  {uploading ? (
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <CloudUploadIcon size={14} />
                  )}
                  Upload
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="overflow-y-auto max-h-72">
          {loading ? (
            <p className="px-3 py-4 text-center text-sm text-slate-400 animate-pulse">
              Loading documents...
            </p>
          ) : documents.length === 0 ? (
            <div className="px-3 py-8 text-center">
              <DocumentsIcon
                size={28}
                className="mx-auto text-indigo-200 mb-2"
                aria-hidden
              />
              <p className="text-sm font-medium text-slate-600">
                No documents yet
              </p>
              <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
                {canManage
                  ? "Upload patient files using the form above."
                  : "No documents have been uploaded for this chart."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className={showActions ? "w-[24%]" : "w-[28%]"} />
                <col className={showActions ? "w-[16%]" : "w-[18%]"} />
                <col className={showActions ? "w-[18%]" : "w-[22%]"} />
                <col className={showActions ? "w-[12%]" : "w-[14%]"} />
                <col className={showActions ? "w-[16%]" : "w-[18%]"} />
                {showActions ? <col className="w-[14%]" /> : null}
              </colgroup>
              <thead
                className={`sticky top-0 z-10 ${PATIENT_THEME.tableHead.row}`}
              >
                <tr
                  className={`border-b text-left text-[11px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-3 py-2 font-semibold">Document Name</th>
                  <th className="px-3 py-2 font-semibold">Category</th>
                  <th className="px-3 py-2 font-semibold hidden md:table-cell">
                    Upload By
                  </th>
                  <th className="px-3 py-2 font-semibold hidden md:table-cell">
                    Role
                  </th>
                  <th className="px-3 py-2 font-semibold hidden sm:table-cell">
                    Upload Date
                  </th>
                  {showActions ? (
                    <th className="px-3 py-2 font-semibold text-right">
                      Action
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                  >
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-900 truncate">
                        {doc.documentName || doc.originalName || "—"}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-slate-600 truncate">
                      {categoryLabel(doc.documentCategory || doc.documentType)}
                    </td>
                    <td className="px-3 py-2 text-slate-600 truncate hidden md:table-cell">
                      {doc.uploadedByName || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 truncate hidden md:table-cell">
                      {doc.uploadedByRole || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap hidden sm:table-cell tabular-nums">
                      {displayDateUS(doc.uploadedAt) || "—"}
                    </td>
                    {showActions ? (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          className="p-1 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded inline-flex"
                          onClick={() => openFile(doc.id, "view")}
                          title="View"
                        >
                          <ViewIcon size={14} />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-50 rounded inline-flex"
                          onClick={() => handleDelete(doc)}
                          title="Delete"
                        >
                          <DeleteIcon size={14} />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {confirmDialog}
      </CollapsibleSection>
    </div>
  );
};

export default PatientDocumentsTab;
